import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import type {
  ApiErrorBody,
  PanelSalesInquiryCountsDto,
  PanelSalesInquiryDetailDto,
  PanelSalesInquiryListDto,
  PanelSalesInquiryListQuery,
  PanelSalesInquiryRowDto,
  SalesInquiryStatus,
} from '@icebreaker/shared';
import { createAsyncActions } from '@icebreaker/shared/store';

/**
 * SATIŞ ADAYLARI DEPOSU (kurum satışla açılır · Story B4).
 *
 * İnvalidation ELLE ve AÇIK (`INVALIDATION_MAP` `panelSales/statusChange` →
 * liste + detay): durum değişince liste sunucudan tazelenir (sekme sayaçları
 * değişir, ikinci bir personel aynı anda başka adayı işaretlemiş olabilir);
 * detay ise yanıtın kendisiyle yerinde güncellenir, ikinci bir detay okuması
 * yapılmaz (her detay okuması denetim defterine "görüntüledi" düşer).
 */
export const fetchSalesInquiries = createAsyncActions<
  PanelSalesInquiryListQuery,
  PanelSalesInquiryListDto
>('panelSales/listFetch');
export const fetchSalesInquiry = createAsyncActions<{ id: string }, PanelSalesInquiryDetailDto>(
  'panelSales/detailFetch',
);
export const changeSalesInquiryStatus = createAsyncActions<
  { id: string; status: SalesInquiryStatus },
  PanelSalesInquiryDetailDto
>('panelSales/statusChange');
/**
 * KENAR MENÜ ROZETİ (panel küçük işler 1 · B-01): yalnız durum sayıları. Kişi
 * taşımaz ve denetim satırı yazmaz; kabuk her açılışta ve pencereye dönüşte
 * okur.
 */
export const fetchSalesCounts = createAsyncActions<void, PanelSalesInquiryCountsDto>(
  'panelSales/countsFetch',
);
/** "Kurum aç" diyaloğunun aday seçicisi ve kurum aramasının aday ipucu. */
export const fetchSalesInquiryPicker = createAsyncActions<
  PanelSalesInquiryListQuery,
  PanelSalesInquiryListDto
>('panelSales/pickerFetch');

export type SalesStatusFilter = SalesInquiryStatus | 'all';

export interface SalesFilters {
  readonly status: SalesStatusFilter;
  readonly q: string;
  readonly page: number;
}

export const INITIAL_SALES_FILTERS: SalesFilters = { status: 'all', q: '', page: 1 };

type Phase = 'idle' | 'loading' | 'succeeded' | 'failed';

export interface SalesInquiriesState {
  list: {
    items: PanelSalesInquiryRowDto[];
    total: number;
    page: number;
    pageSize: number;
    hasNext: boolean;
    statusCounts: Record<SalesInquiryStatus, number> | null;
    phase: Phase;
    error: ApiErrorBody | null;
  };
  filters: SalesFilters;
  detail: {
    id: string | null;
    data: PanelSalesInquiryDetailDto | null;
    phase: Phase;
    error: ApiErrorBody | null;
  };
  statusChange: {
    phase: Phase;
    error: ApiErrorBody | null;
    /** Hata anında geri yüklenen durum — kullanıcıya "eski durum" diye yazılır. */
    restored: SalesInquiryStatus | null;
  };
  picker: {
    items: PanelSalesInquiryRowDto[];
    query: string;
    phase: Phase;
  };
  /**
   * "Yeni" durumundaki aday sayısı. `null` = henüz bilinmiyor (rozet çizilmez,
   * sayı uydurulmaz). Sayı ucu ya da ARAMASIZ liste cevabı yazar; aramalı
   * listenin sayıları süzülmüştür ve rozete yazılmaz.
   */
  newCount: number | null;
}

const initialState: SalesInquiriesState = {
  list: {
    items: [],
    total: 0,
    page: 1,
    pageSize: 0,
    hasNext: false,
    statusCounts: null,
    phase: 'idle',
    error: null,
  },
  filters: INITIAL_SALES_FILTERS,
  detail: { id: null, data: null, phase: 'idle', error: null },
  statusChange: { phase: 'idle', error: null, restored: null },
  picker: { items: [], query: '', phase: 'idle' },
  newCount: null,
};

const slice = createSlice({
  name: 'panelSales',
  initialState,
  reducers: {
    /** Süzgeç değişince sayfa 1'e döner (dördüncü sayfada boş liste yalandır). */
    filterChanged(state, action: PayloadAction<Partial<SalesFilters>>) {
      const next = { ...state.filters, ...action.payload };
      state.filters = action.payload.page === undefined ? { ...next, page: 1 } : next;
    },
    filtersReset(state) {
      state.filters = INITIAL_SALES_FILTERS;
    },
    detailClosed(state) {
      state.detail = { id: null, data: null, phase: 'idle', error: null };
      state.statusChange = { phase: 'idle', error: null, restored: null };
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchSalesInquiries.request, (state) => {
        state.list.phase = 'loading';
        state.list.error = null;
      })
      .addCase(fetchSalesInquiries.success, (state, action) => {
        state.list.phase = 'succeeded';
        state.list.items = action.payload.items.map((item) => ({ ...item }));
        state.list.total = action.payload.total;
        state.list.page = action.payload.page;
        state.list.pageSize = action.payload.pageSize;
        state.list.hasNext = action.payload.hasNext;
        state.list.statusCounts = { ...action.payload.statusCounts };
        if (state.filters.q.trim() === '') state.newCount = action.payload.statusCounts.new;
      })
      .addCase(fetchSalesInquiries.failure, (state, action) => {
        state.list.phase = 'failed';
        state.list.error = action.payload;
      })
      .addCase(fetchSalesInquiry.request, (state, action) => {
        // Başka bir adaya geçilince önceki adayın verisi ve kayıt sonucu ekranda
        // KALMAZ. Aynı adayın tazelenmesi (sonucu bilinmeyen kayıttan sonra)
        // "doğrulanamadı" uyarısını SİLMEZ.
        if (state.detail.id !== action.payload.id) {
          state.detail.data = null;
          state.statusChange = { phase: 'idle', error: null, restored: null };
        }
        state.detail.id = action.payload.id;
        state.detail.phase = 'loading';
        state.detail.error = null;
      })
      .addCase(fetchSalesInquiry.success, (state, action) => {
        if (state.detail.id !== action.payload.id) return;
        state.detail.phase = 'succeeded';
        state.detail.data = cloneDetail(action.payload);
      })
      .addCase(fetchSalesInquiry.failure, (state, action) => {
        state.detail.phase = 'failed';
        state.detail.error = action.payload;
      })
      .addCase(changeSalesInquiryStatus.request, (state) => {
        state.statusChange = { phase: 'loading', error: null, restored: null };
      })
      .addCase(changeSalesInquiryStatus.success, (state, action) => {
        state.statusChange = { phase: 'succeeded', error: null, restored: null };
        if (state.detail.id === action.payload.id) state.detail.data = cloneDetail(action.payload);
      })
      .addCase(changeSalesInquiryStatus.failure, (state, action) => {
        state.statusChange = {
          phase: 'failed',
          error: action.payload,
          restored: state.detail.data?.status ?? null,
        };
      })
      .addCase(fetchSalesCounts.success, (state, action) => {
        state.newCount = action.payload.statusCounts.new;
      })
      // Sayı alınamadıysa son bilinen değer kalır; hiç bilinmiyorsa rozet yok.
      .addCase(fetchSalesInquiryPicker.request, (state, action) => {
        state.picker.phase = 'loading';
        state.picker.query = action.payload.q ?? '';
      })
      .addCase(fetchSalesInquiryPicker.success, (state, action) => {
        state.picker.phase = 'succeeded';
        state.picker.items = action.payload.items.map((item) => ({ ...item }));
      })
      .addCase(fetchSalesInquiryPicker.failure, (state) => {
        state.picker.phase = 'failed';
        state.picker.items = [];
      });
  },
});

/** Salt okunur DTO dizilerini depoya yazılabilir kopyayla koyar. */
function cloneDetail(detail: PanelSalesInquiryDetailDto) {
  return {
    ...detail,
    organization: detail.organization ? { ...detail.organization } : null,
    linkedUser: detail.linkedUser ? { ...detail.linkedUser } : null,
    history: detail.history.map((entry) => ({ ...entry, staff: { ...entry.staff } })),
  };
}

export const {
  filterChanged: salesFilterChanged,
  filtersReset: salesFiltersReset,
  detailClosed: salesDetailClosed,
} = slice.actions;
export const salesInquiriesReducer = slice.reducer;
